"use client"
import styled from "@emotion/styled"
import { Box, IconButton } from "@mui/material"
import { GetPageByRouteQuery } from "apollo/generated/graphql"
import { useRef, useState } from "react"
import {
  Container,
  isVideo,
  NewText,
  PauseIcon,
  Play2Icon,
  ResponsiveMedia,
  useIsDesktop,
} from "ui"
import { extractProperty, getNonUmbracoUrl, getUmbracoMediaUrl, replaceRichTextLinks } from "utils"
import NewLobbyButton from "./NewLobbyButton"

const StyledBannerContainer = styled.section`
  width: 100%;
  display: flex;
`

const StyledMedia = styled.div`
  display: flex;
  width: 100%;
  height: 500px;
  position: relative;
  top: 0;
  left: 0;
  ${(props) => props.theme.breakpoints.up("lg")} {
    height: 600px;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    height: 750px;
  }
  > div {
    width: 100%;
  }
  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ControlButton = styled(IconButton)<{ $isPlaying: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  padding: 0;
  background-color: rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease-out;
  color: white;
  & svg {
    color: inherit;
  }
  &:hover {
    background-color: #0e2846;
    color: white;
  }
`

const StyledContent = styled.div`
  position: absolute;
  z-index: 2;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  color: white;
  .lobby-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding: 40px 8.5px;
    ${(props) => props.theme.breakpoints.up("md")} {
      padding: 60px 20px;
    }
  }
`

const StyledBgOverlay = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  background-image: linear-gradient(180deg, rgba(5, 41, 70, 0) 0.43%, #052946 99.67%);
  ${(props) => props.theme.breakpoints.up("md")} {
    background-image: linear-gradient(90deg, rgba(5, 41, 70, 0) 27.53%, #052946 99.11%);
  }
`

type Props = {
  pageData: GetPageByRouteQuery
  hero?: {
    desktopMedia?: any
    mobileMedia?: any
    bannerTitle?: any
    bannerText?: any
    button?: any
    altText?: string
  }
}

const LobbyBanner = ({ pageData, hero }: Props) => {
  const isDesktop = useIsDesktop()
  // banner data
  let desktopMedia =
    hero?.desktopMedia ||
    extractProperty(pageData.page, "imageDscktop") ||
    extractProperty(pageData.page, "imageDesktop")
  let mobileMedia = hero?.mobileMedia || extractProperty(pageData.page, "imageMobile")
  const bannerTitle = hero?.bannerTitle || extractProperty(pageData.page, "bannerTitle")
  const bannerText = hero?.bannerText || extractProperty(pageData.page, "bannerText")
  const button = hero?.button || extractProperty(pageData.page, "button")?.[0]

  if (!desktopMedia && mobileMedia) {
    desktopMedia = mobileMedia
  }

  if (!mobileMedia && desktopMedia) {
    mobileMedia = desktopMedia
  }
  const altText =
    hero?.altText || extractProperty(pageData.page, "bannerImageAltText") || "Lobby Banner"

  // video playback state
  const [isPlaying, setIsPlaying] = useState(true)
  const desktopVideoRef = useRef<HTMLVideoElement>(null)
  const mobileVideoRef = useRef<HTMLVideoElement>(null)

  const hasVideoMedia = isDesktop ? isVideo(desktopMedia) : isVideo(mobileMedia)

  const syncPlaybackState = (shouldPlay: boolean) => {
    // eslint-disable-next-line no-extra-semi
    ;[desktopVideoRef.current, mobileVideoRef.current].forEach((video) => {
      if (!video) return
      if (shouldPlay) {
        const playPromise = video.play()
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => undefined)
        }
      } else {
        video.pause()
      }
    })
  }

  const handlePlaybackToggle = () => {
    setIsPlaying((prev) => {
      const next = !prev
      syncPlaybackState(next)
      return next
    })
  }

  return (
    <StyledBannerContainer>
      <StyledMedia>
        <ResponsiveMedia
          desktopMedia={getUmbracoMediaUrl(desktopMedia)}
          mobileMedia={getUmbracoMediaUrl(mobileMedia)}
          alt={altText}
          priority
          desktopVideoRef={desktopVideoRef}
          mobileVideoRef={mobileVideoRef}
        />
        <StyledBgOverlay />
        {hasVideoMedia && (
          <Box
            component={"span"}
            sx={{
              position: { xs: "absolute" },
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 3,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: "1080px",
                display: "flex",
                justifyContent: {
                  xs: "flex-start",
                  md: "flex-end",
                },
                padding: {
                  xs: "40px 20px",
                },
              }}
            >
              <ControlButton
                aria-label={isPlaying ? "Pause background video" : "Play background video"}
                onClick={handlePlaybackToggle}
                disableRipple
                $isPlaying={isPlaying}
              >
                {isPlaying ? <PauseIcon /> : <Play2Icon />}
              </ControlButton>
            </Box>
          </Box>
        )}
        <StyledContent>
          <Container className="lobby-container" width={1080}>
            {bannerTitle && (
              <NewText
                color="inherit"
                component="h1"
                desktopFontSize={48}
                mobileFontSize={40}
                desktopLineHeight={"normal"}
                mobileLineHeight={"46px"}
                desktopFontWeight={600}
                sx={{
                  margin: 0,
                  padding: 0,
                  marginBottom: "4px",
                }}
              >
                {bannerTitle}
              </NewText>
            )}
            {bannerText && (
              <NewText
                color="inherit"
                component="div"
                desktopFontSize={24}
                mobileFontSize={20}
                desktopLineHeight={"normal"}
                mobileLineHeight={"normal"}
                desktopFontWeight={400}
                sx={{
                  margin: 0,
                  padding: 0,
                  marginBottom: {
                    xs: "8px",
                    md: "16px",
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: replaceRichTextLinks(bannerText),
                }}
              />
            )}
            {button?.url && button?.name && (
              <NewLobbyButton
                variant="outlined"
                target={
                  button.target ? button.target : button.type === "EXTERNAL" ? "_blank" : "_self"
                }
                href={getNonUmbracoUrl(button.url)}
                sx={{
                  alignSelf: {
                    xs: "flex-end",
                    md: "flex-start",
                  },
                }}
              >
                {button.name}
              </NewLobbyButton>
            )}
          </Container>
        </StyledContent>
      </StyledMedia>
    </StyledBannerContainer>
  )
}
export default LobbyBanner
